/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / STATS — Statistik-Gerüst (S180 Auswertung-Rework, Cut 1)
   =============================================================================
   Neues Gerüst: GENAU 3 Bereiche (Tagesgeschäft · Warenwirtschaft · Personal) +
   zentraler Zeitraum-Filter (Heute/7T/30T/Monat/frei) + leere Dashboard-Shells.
   Die echten Kennzahlen kommen in Cut 2 (Tagesgeschäft) / Cut 3 (Warenwirtschaft)
   / Cut 4 (Personal). E-Mails gehören NICHT hierher (Archiv-Tab, Cut 6).

   Inline-Handler als func() über dom-bindings (CSP, Memory gg-inline-handler-csp):
   die Namen werden unten per Object.assign(window,…) registriert, im HTML OHNE
   window.-Prefix aufgerufen.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { rangeForPreset, defaultPreset } from '../../core/date-range.js';
import { renderTagesgeschaeftInto } from './stats-tagesgeschaeft.js';
import { renderWarenwirtschaftInto } from './stats-warenwirtschaft.js';
import { renderPersonalInto } from './stats-personal.js';

// Genau 3 Bereiche — Reihenfolge fix (Vision §Statistik).
const AREAS = Object.freeze([
  { id: 'tagesgeschaeft',  label: 'Tagesgeschäft',   cut: 'Cut 2' },
  { id: 'warenwirtschaft', label: 'Warenwirtschaft', cut: 'Cut 3' },
  { id: 'personal',        label: 'Personal',        cut: 'Cut 4' }
]);
const AREA_IDS = AREAS.map(a => a.id);

// Zeitraum-Presets (Labels für die Filter-Leiste).
const RANGE_BUTTONS = Object.freeze([
  { preset: 'today', label: 'Heute' },
  { preset: '7d',    label: '7 Tage' },
  { preset: '30d',   label: '30 Tage' },
  { preset: 'month', label: 'Monat' },
  { preset: 'custom', label: 'frei' }
]);

const _statsState = {
  area:  'tagesgeschaeft',
  range: rangeForPreset(defaultPreset)    // { preset, from, to }
};

/* -------------------------------------------------------------------------- */
/* Public-API (über dom-bindings)                                              */
/* -------------------------------------------------------------------------- */

export function setStatsArea(area) {
  if (!AREA_IDS.includes(area)) return;
  _statsState.area = area;
  renderStats();
}

export function setStatsRange(preset) {
  if (preset === 'custom') {
    // Beim Wechsel auf „frei" die aktuellen Grenzen als Startwerte übernehmen
    // (Inputs nicht leer), Aufrufer kann dann verfeinern.
    const cur = _statsState.range || {};
    _statsState.range = rangeForPreset('custom', new Date(), { from: cur.from, to: cur.to });
    // Falls vorher kein konkreter Zeitraum gesetzt war → 7d als Default-Fenster.
    if (!_statsState.range.from || !_statsState.range.to) {
      const d = rangeForPreset('7d');
      _statsState.range = { preset: 'custom', from: d.from, to: d.to };
    }
  } else {
    _statsState.range = rangeForPreset(preset);
  }
  renderStats();
}

/** „frei"-Datumsänderung: NUR den Ergebnis-Teil neu rendern (Datumsfelder +
 *  Fokus bleiben erhalten — Memory gg-non-destructive-render). */
export function statsSetCustomDate(which, value) {
  if (!_statsState.range || _statsState.range.preset !== 'custom') return;
  if (which === 'from') _statsState.range.from = value || null;
  else if (which === 'to') _statsState.range.to = value || null;
  const result = document.getElementById('stats-result');
  if (result) { result.innerHTML = _renderResultHtml(); _afterResultRender(); }
}

/* -------------------------------------------------------------------------- */
/* Render                                                                      */
/* -------------------------------------------------------------------------- */

function _areaSwitcherHtml() {
  return `
    <div class="stats-subtabs" role="tablist" style="display:flex; gap:6px; margin-bottom:12px; flex-wrap:wrap;">
      ${AREAS.map(a => `
        <button class="btn ${_statsState.area === a.id ? 'btn-primary' : 'btn-ghost'} btn-sm"
                role="tab" aria-selected="${_statsState.area === a.id}"
                onclick="setStatsArea('${a.id}')">${htmlEscape(a.label)}</button>
      `).join('')}
    </div>`;
}

function _rangeFilterHtml() {
  const r = _statsState.range || {};
  const buttons = RANGE_BUTTONS.map(b => `
    <button class="btn ${r.preset === b.preset ? 'btn-primary' : 'btn-secondary'} btn-sm"
            onclick="setStatsRange('${b.preset}')">${htmlEscape(b.label)}</button>
  `).join('');
  const customInputs = (r.preset === 'custom') ? `
    <span class="stats-custom-range" style="display:inline-flex; align-items:center; gap:6px; margin-left:6px;">
      <input type="date" value="${htmlEscape(r.from || '')}" onchange="statsSetCustomDate('from', this.value)"
             style="padding:4px 8px;" aria-label="Von">
      <span style="color:var(--ink-soft);">–</span>
      <input type="date" value="${htmlEscape(r.to || '')}" onchange="statsSetCustomDate('to', this.value)"
             style="padding:4px 8px;" aria-label="Bis">
    </span>` : '';
  return `
    <div class="stats-toolbar" style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:14px;">
      <strong>Zeitraum:</strong>
      ${buttons}
      ${customInputs}
    </div>`;
}

/** Inhalt des aktiven Bereichs. Alle drei Bereiche (Tagesgeschäft/Warenwirtschaft/
 *  Personal) werden async befüllt (_afterResultRender → render*Into). */
function _renderResultHtml() {
  const area = AREAS.find(a => a.id === _statsState.area) || AREAS[0];
  const r = _statsState.range || {};
  const rangeLabel = (r.preset === 'custom')
    ? ((r.from && r.to) ? `${r.from} – ${r.to}` : 'Zeitraum wählen')
    : (RANGE_BUTTONS.find(b => b.preset === r.preset) || {}).label || '';

  const head = `
    <div class="stats-area-head" style="display:flex; align-items:baseline; gap:10px; margin-bottom:10px; flex-wrap:wrap;">
      <h3 style="margin:0;">${htmlEscape(area.label)}</h3>
      <span class="stats-sub" style="color:var(--ink-soft); font-size:.85rem;">${htmlEscape(rangeLabel)}</span>
    </div>`;

  // Cut 2: Tagesgeschäft = echtes Dashboard (async befüllt).
  if (area.id === 'tagesgeschaeft') {
    return `${head}<div id="stats-tg"><p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p></div>`;
  }

  // Cut 3: Warenwirtschaft = echtes Dashboard (async befüllt).
  if (area.id === 'warenwirtschaft') {
    return `${head}<div id="stats-ww"><p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p></div>`;
  }

  // Cut 4: Personal = echtes Dashboard (async befüllt).
  if (area.id === 'personal') {
    return `${head}<div id="stats-personal"><p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p></div>`;
  }

  // Fallback (sollte mit 3 echten Bereichen nicht greifen).
  return `${head}<div class="card" style="margin-top:16px;">
    <p style="color:var(--ink-soft); margin:0; text-align:center; padding:24px 12px;">Keine Daten.</p></div>`;
}

/** Nach jedem Result-Render: aktiven Bereich ggf. async befüllen. */
function _afterResultRender() {
  if (_statsState.area === 'tagesgeschaeft') {
    const host = document.getElementById('stats-tg');
    if (host) renderTagesgeschaeftInto(host, _statsState.range);
  } else if (_statsState.area === 'warenwirtschaft') {
    const host = document.getElementById('stats-ww');
    if (host) renderWarenwirtschaftInto(host, _statsState.range);
  } else if (_statsState.area === 'personal') {
    const host = document.getElementById('stats-personal');
    if (host) renderPersonalInto(host, _statsState.range);
  }
}

/** Einstieg — gemountet in #tab-stats (view-switch-Wiring unverändert). */
export function renderStats() {
  const tab = document.getElementById('tab-stats');
  if (!tab) return;
  tab.innerHTML = `
    ${_areaSwitcherHtml()}
    ${_rangeFilterHtml()}
    <div id="stats-result">${_renderResultHtml()}</div>
  `;
  _afterResultRender();
}

/* Test-Hook */
export function _resetForTest() {
  _statsState.area = 'tagesgeschaeft';
  _statsState.range = rangeForPreset(defaultPreset);
}

/* -------------------------------------------------------------------------- */
/* window-Bridge (CSP: inline func())                                          */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderStats,
    setStatsArea,
    setStatsRange,
    statsSetCustomDate
  });
}
